/**
 * Typed fetch wrappers. One place to handle auth errors, JSON parsing,
 * and base URL.
 */

// Canonical ignore-pattern presets seeded into a new depot/stream's
// Ignored: field (+ .p4ignore for depots). Mirrors the backend
// IgnorePreset in admin/ignorePresets.ts. `none` seeds nothing.
export type IgnorePreset = 'none' | 'ue5' | 'roblox';

export const IGNORE_PRESET_OPTIONS: ReadonlyArray<{ value: IgnorePreset; label: string }> = [
  { value: 'ue5', label: 'UE5 / UEFN' },
  { value: 'roblox', label: 'Roblox' },
  { value: 'none', label: 'None' },
];

// --- Lore VCS (task #1) ---------------------------------------------------
// Response shapes deliberately mirror the depot equivalents so shared
// components (thumbnails, preview modal) consume either source.

// The dashboard-side path prefix for Lore content. Single source of
// truth for the frontend (mirrors backend src/lore/paths.ts).
export const LORE_PREFIX = '//_lore/';

export interface LoreRepoInfo {
  name: string;
  description: string | null;
  /** Dashboard path for the repo root: //_lore/<name> */
  path: string;
}

export interface LoreReposResponse {
  repos: LoreRepoInfo[];
}

export interface LoreDirEntry {
  path: string; // full //_lore/... path
}

export interface LoreFileEntry {
  /** Full //_lore/... path (mirrors DepotEntry.depotFile). */
  depotFile: string;
  /**
   * Always null in listings — an honest per-file revision would cost a
   * `file history` spawn per file. The preview modal resolves the real
   * rev via loreFstat.
   */
  rev: number | null;
  fileSize: number | null;
  /** ISO timestamp of the revision, when known. */
  time: string | null;
}

export interface LoreListResponse {
  path: string;
  dirs: LoreDirEntry[];
  files: LoreFileEntry[];
}

export interface LoreFstatResponse {
  depotFile: string;
  headRev: number;
  fileSize: number | null;
  headTime: string | null;
}

export interface LoreRevision {
  revision: number;
  /** 64-hex signature; the id `--revision` specifiers accept. */
  signature: string;
  message: string;
  /** Commit creator (p4 username), or null if the CLI didn't print one. */
  author: string | null;
  time: string | null;
}

// --- self-hosted git (Forgejo) ---------------------------------------------
export interface GitRepo {
  fullName: string;
  owner: string;
  name: string;
  description: string;
  defaultBranch: string;
  private: boolean;
  updatedAt: string | null;
}
export interface GitTreeResponse {
  repo: string;
  path: string;
  dirs: Array<{ name: string; path: string }>;
  files: Array<{ name: string; path: string; size: number }>;
}
export interface GitCommit {
  sha: string;
  message: string;
  author: string | null;
  time: string | null;
}

// --- omni search (file_index) ----------------------------------------------
export type OmniSource = 'p4' | 'lore' | 'git';
export interface OmniHit {
  source: OmniSource;
  /** p4: //Depot/...  lore: //_lore/<repo>/...  git: <owner>/<repo>/... */
  path: string;
  name: string;
  rev: number | null;
  change: number | null;
  action: string | null;
  fileType: string | null;
  fileSize: number | null;
  changedBy: string | null;
  changedAt: string | null;
}
export interface OmniSearchResponse {
  q: string;
  results: OmniHit[];
  total: number;
}

export interface LoreHistoryResponse {
  repo: string;
  revisions: LoreRevision[];
}

export interface HealthResponse {
  backend: string;
  p4d: {
    reachable: boolean;
    version: string | null;
    serverVersion: string | null;
    serverAddress: string | null;
    caseHandling: string | null;
    license: string | null;
    error: string | null;
  };
}

export interface MeResponse {
  user: string;
  createdAt: string;
  isAdmin: boolean;
}

export interface AdminAction {
  id: number;
  actionType: string;
  actor: string;
  payload: Record<string, unknown>;
  status: 'running' | 'success' | 'failed' | 'partial';
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface ColdStorageRun {
  id: number;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'failed';
  triggeredBy: string | null;
  objects: number | null;
  bytes: number | null;
  listCalls: number | null;
  error: string | null;
}

export interface ColdStorageStatus {
  current: ColdStorageRun | null; // newest successful listing (what the browser shows)
  latest: ColdStorageRun | null; // newest attempt, any status
  running: boolean;
}

export interface ColdStorageListing {
  path: string;
  dirs: { name: string; bytes: number; objects: number; newest: string | null }[];
  files: { name: string; size: number; mtime: string | null }[];
  filesTotal: number;
}

export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
    this.name = 'UnauthorizedError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (res.status === 401) {
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    throw new Error(`${path} returned ${res.status}: ${await res.text()}`);
  }
  // 204 No Content endpoints (e.g. DELETE /api/depot/tags) have empty
  // bodies — `res.json()` would throw. Treat as void.
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export interface UserRow {
  user: string;
  email: string | null;
  fullName: string | null;
  type: string | null;
  updateAt: string | null;
  accessAt: string | null;
}

export interface UserDetail {
  user: string;
  fullName: string | null;
  email: string | null;
  type: string | null;
  accessAt: string | null;
  updateAt: string | null;
  workspaces: Array<{
    client: string;
    host: string | null;
    root: string | null;
    description: string | null;
    updateAt: string | null;
    accessAt: string | null;
  }>;
  openedFiles: Array<{
    depotFile: string;
    rev: number | null;
    action: string | null;
    type: string | null;
    change: string | null;
    client: string | null;
  }>;
  recentChanges: Array<{
    change: number;
    time: string | null;
    client: string | null;
    description: string;
  }>;
  activity: Array<{ date: string; count: number }>;
}

export interface WorkspaceRow {
  client: string;
  owner: string | null;
  host: string | null;
  root: string | null;
  description: string | null;
  updateAt: string | null;
  accessAt: string | null;
}

export interface ChangeRow {
  change: number;
  user: string | null;
  client: string | null;
  status: string | null;
  time: string | null;
  description: string;
  // Depot(s) + stream(s) the change touched (usually one each).
  depots?: string[];
  streams?: string[];
}

export interface ChangeDetail extends ChangeRow {
  files: Array<{
    depotFile: string;
    rev: number | null;
    action: string | null;
    type: string | null;
  }>;
}

export interface DepotEntry {
  depotFile: string;
  rev: number | null;
  action: string | null;
  type: string | null;
  change: number | null;
  time: string | null;
}

/**
 * Child directory inside a depot listing. The `stream` field is non-null
 * when this directory is actually a Perforce stream (mainline, release,
 * task, etc.) rather than a plain folder — we render those differently
 * so navigators can see the branch structure.
 */
export interface DepotDir {
  path: string;
  stream: { type: string; parent: string | null } | null;
  // Populated only at the // root listing — per-depot metadata for
  // the depots-overview view.
  depotInfo?: {
    type: string;
    description: string;
    createdAt: string | null;
    fileCount: number | null;
    fileSize: number | null;
    latestChange: number | null;
    latestChangeAt: string | null;
    latestChangeUser: string | null;
  };
}

export interface DepotListResponse {
  path: string;
  dirs: DepotDir[];
  files: DepotEntry[];
}

/** Newest submitted change under a path (recursive). `change` is null for empty paths. */
export interface DepotLatestChange {
  path: string;
  change: number | null;
  user?: string | null;
  time?: string | null;
  description?: string;
}

export interface FstatResponse {
  depotFile: string;
  headRev: number | null;
  headChange: number | null;
  headType: string | null;
  headAction: string | null;
  headTime: string | null;
  headModTime: string | null;
  fileSize: number | null;
  digest: string | null;
}

export const api = {
  health: () => request<HealthResponse>('/api/health'),

  me: () => request<MeResponse>('/api/auth/me'),

  // Status-overview surfaces (session-gated).
  statusSeats: () => request<SeatsResponse>('/api/status/seats'),
  statusContentHealth: () => request<ContentHealthResponse>('/api/status/content-health'),
  statusLocks: () => request<StatusLocksResponse>('/api/status/locks'),

  login: (user: string, password: string) =>
    request<{ user: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ user, password }),
    }),

  logout: () =>
    request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  users: () => request<{ users: UserRow[] }>('/api/users'),

  userDetail: (user: string) =>
    request<UserDetail>(`/api/users/${encodeURIComponent(user)}`),

  workspaces: () => request<{ workspaces: WorkspaceRow[] }>('/api/workspaces'),

  changes: (opts: { max?: number | undefined; limit?: number | undefined } = {}) => {
    const params = new URLSearchParams();
    if (opts.max) params.set('max', String(opts.max));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request<{ changes: ChangeRow[] }>(`/api/changes${qs}`);
  },

  changeDetail: (n: number) => request<ChangeDetail>(`/api/changes/${n}`),

  depotList: (path: string) =>
    request<DepotListResponse>(`/api/depot/list?path=${encodeURIComponent(path)}`),

  depotLatestChange: (path: string) =>
    request<DepotLatestChange>(`/api/depot/latest-change?path=${encodeURIComponent(path)}`),

  depotSearch: (
    path: string,
    q: string,
    opts: {
      // Each entry is one OR-group (multiple comma-separated tag
      // names within a group; multiple groups AND together).
      include?: string[][];
      // Flat list — any of these excludes the asset.
      exclude?: string[];
      // Asset class filter — flat OR-list of UE class names.
      includeClass?: string[];
      excludeClass?: string[];
      // Full-text query over preview.json properties.
      propQuery?: string;
      // Budget facet: 'over' (strictly over budget) or 'large' (warn+over).
      budget?: 'over' | 'large';
      // Broken-reference facet: restrict to assets with unresolved imports.
      brokenRefs?: boolean;
      limit?: number;
    } = {},
  ) => {
    const params = new URLSearchParams({ path, q });
    for (const group of opts.include ?? []) {
      params.append('include', group.join(','));
    }
    if (opts.exclude && opts.exclude.length > 0) {
      params.set('exclude', opts.exclude.join(','));
    }
    if (opts.includeClass && opts.includeClass.length > 0) {
      params.set('class', opts.includeClass.join(','));
    }
    if (opts.excludeClass && opts.excludeClass.length > 0) {
      params.set('excludeClass', opts.excludeClass.join(','));
    }
    if (opts.propQuery && opts.propQuery.trim().length > 0) {
      params.set('prop', opts.propQuery.trim());
    }
    if (opts.budget) {
      params.set('budget', opts.budget);
    }
    if (opts.brokenRefs) {
      params.set('brokenrefs', '1');
    }
    params.set('limit', String(opts.limit ?? 50));
    return request<{
      results: DepotEntry[];
      total: number;
      capped: boolean;
      scanned: number;
    }>(`/api/depot/search?${params.toString()}`);
  },

  classForPath: (path: string) =>
    request<AssetClassResponse>(`/api/depot/class?path=${encodeURIComponent(path)}`),

  depotFacets: (path: string) =>
    request<FacetsResponse>(`/api/depot/facets?path=${encodeURIComponent(path)}`),

  depotActivity: (path: string) =>
    request<DepotActivityResponse>(`/api/depot/activity?path=${encodeURIComponent(path)}`),

  tagsForPath: (path: string) =>
    request<TagsResponse>(`/api/depot/tags?path=${encodeURIComponent(path)}`),

  tagSuggestions: (q: string, limit = 10) =>
    request<{ suggestions: Array<{ tag: string; useCount: number }> }>(
      `/api/depot/tags/suggest?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),

  addTag: (path: string, tag: string) =>
    request<AddTagResponse>(`/api/depot/tags`, {
      method: 'POST',
      body: JSON.stringify({ path, tag }),
    }),

  gcOrphanTags: () =>
    request<{ scanned: number; deleted: number; capped: boolean; errors: string[] }>(
      `/api/admin/tags/gc`,
      // POST with no body — request() omits Content-Type when body
      // is undefined, so Fastify's JSON parser doesn't try to read
      // a stream that isn't there.
      { method: 'POST' },
    ),

  removeTag: (path: string, tag: string) =>
    request<void>(
      `/api/depot/tags?path=${encodeURIComponent(path)}&tag=${encodeURIComponent(tag)}`,
      { method: 'DELETE' },
    ),

  fstat: (path: string) =>
    request<FstatResponse>(`/api/depot/fstat?path=${encodeURIComponent(path)}`),

  fileUrl: (path: string, rev?: number) => {
    // Lore paths stream from the lore backend; everything else is
    // `p4 print`. Branching here (not at call sites) means AssetPreview's
    // raw-download link works for both sources transparently.
    const base = path.startsWith(LORE_PREFIX) ? '/api/lore/file' : '/api/depot/file';
    return rev !== undefined
      ? `${base}?path=${encodeURIComponent(path)}&rev=${rev}`
      : `${base}?path=${encodeURIComponent(path)}`;
  },

  // --- Lore VCS pilot (task #1) — read-only browse ---------------------
  omniSearch: (q: string, limit = 100) =>
    request<OmniSearchResponse>(`/api/search?q=${encodeURIComponent(q)}&limit=${String(limit)}`),

  gitRepos: () => request<{ repos: GitRepo[] }>(`/api/git/repos`),

  gitTree: (repo: string, path: string, ref?: string) =>
    request<GitTreeResponse>(
      `/api/git/tree?repo=${encodeURIComponent(repo)}&path=${encodeURIComponent(path)}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`,
    ),

  gitLatest: (repo: string, path: string, ref?: string) =>
    request<{ repo: string; path: string; commit: GitCommit | null }>(
      `/api/git/latest?repo=${encodeURIComponent(repo)}&path=${encodeURIComponent(path)}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`,
    ),

  gitFileUrl: (repo: string, path: string, ref?: string) =>
    `/api/git/file?repo=${encodeURIComponent(repo)}&path=${encodeURIComponent(path)}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`,

  loreRepos: () => request<LoreReposResponse>(`/api/lore/repos`),

  loreList: (path: string) =>
    request<LoreListResponse>(`/api/lore/list?path=${encodeURIComponent(path)}`),

  loreFstat: (path: string) =>
    request<LoreFstatResponse>(`/api/lore/fstat?path=${encodeURIComponent(path)}`),

  loreHistory: (repo: string, limit = 20) =>
    request<LoreHistoryResponse>(
      `/api/lore/history?repo=${encodeURIComponent(repo)}&limit=${String(limit)}`,
    ),

  loreToken: () =>
    request<{ user: string; expiresInDays: number; token: string; loginCommand: string }>(
      `/api/lore/token`,
      { method: 'POST' },
    ),

  locks: (path: string) =>
    request<LocksResponse>(`/api/depot/locks?path=${encodeURIComponent(path)}`),

  history: (path: string, limit = 50) =>
    request<HistoryResponse>(
      `/api/depot/history?path=${encodeURIComponent(path)}&limit=${limit}`,
    ),

  damThumbnailUrl: (path: string, rev: number, size: 256 | 512 = 256) =>
    `/api/dam/thumbnail?path=${encodeURIComponent(path)}&rev=${rev}&size=${size}`,

  damPreviewUrl: (path: string, rev: number, ext?: string) =>
    `/api/dam/preview?path=${encodeURIComponent(path)}&rev=${rev}` +
    (ext ? `&ext=${encodeURIComponent(ext)}` : ''),

  damJob: (id: number) =>
    request<{ id: number; state: DamJobState; error: string | null; attempts: number }>(
      `/api/dam/job/${id}`,
    ),

  // Unified preview-modal read endpoint — aggregates dam_jobs row state,
  // cache artifact inventory, current renderer code version, and the
  // meta.json blob in one response. The frontend's useDamReady hook
  // drives off this; ops scripts use the same response shape to verify
  // what the modal would render without screen-scraping.
  damAssetPreview: (path: string, rev: number) =>
    request<DamAssetPreviewResponse>(
      `/api/dam/asset-preview?path=${encodeURIComponent(path)}&rev=${rev}`,
    ),

  adminJobsSummary: () =>
    request<JobsSummary>(`/api/admin/jobs/summary`),

  adminRetryFailedJobs: () =>
    request<{ retried: number }>(`/api/admin/jobs/retry-failed`, { method: 'POST' }),

  adminRetryAsset: (depotPath: string, headRev: number, kind: string) =>
    request<{ retried: number; removed: number; keepId: number }>(
      `/api/admin/jobs/retry-asset`,
      {
        method: 'POST',
        body: JSON.stringify({ depotPath, headRev, kind }),
      },
    ),

  adminStorageSummary: (force = false) =>
    request<StorageSummary>(
      `/api/admin/storage/summary${force ? '?force=1' : ''}`,
    ),

  adminStorageTopAssets: (n = 10) =>
    request<{ assets: TopAsset[] }>(
      `/api/admin/storage/top-assets?n=${n}`,
    ),

  adminTagsList: () =>
    request<{ tags: AdminTag[] }>(`/api/admin/tags`),

  adminTagRename: (id: number, newName: string) =>
    request<{ renamed: boolean; name: string; previousName?: string }>(
      `/api/admin/tags/${id}/rename`,
      { method: 'POST', body: JSON.stringify({ newName }) },
    ),

  adminTagMerge: (id: number, intoId: number) =>
    request<{
      merged: boolean;
      sourceName: string;
      targetName: string;
      movedAssignments: number;
      skippedDuplicates: number;
    }>(`/api/admin/tags/${id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ intoId }),
    }),

  adminTagDelete: (id: number) =>
    request<{ deleted: boolean; name: string; affected: number }>(
      `/api/admin/tags/${id}`,
      { method: 'DELETE' },
    ),

  adminTagSetCategory: (id: number, category: TagCategory | null) =>
    request<{ id: number; category: TagCategory | null }>(
      `/api/admin/tags/${id}/category`,
      { method: 'POST', body: JSON.stringify({ category }) },
    ),

  adminTagAudit: () =>
    request<{ entries: TagAuditEntry[] }>(`/api/admin/tags/audit`),

  adminBackupSnapshot: () =>
    request<{ snapshot: BackupSnapshot | null; message?: string }>(
      `/api/admin/backups/snapshot`,
    ),

  adminBackupLogTail: () =>
    request<BackupLogTail>(`/api/admin/backups/log-tail`),

  adminBackupHistory: (hours = 168) =>
    request<BackupHistory>(`/api/admin/backups/history?hours=${hours}`),

  adminColdStatus: () => request<ColdStorageStatus>(`/api/admin/cold-storage/status`),

  adminColdList: (path: string) =>
    request<ColdStorageListing>(`/api/admin/cold-storage/list?path=${encodeURIComponent(path)}`),

  adminColdRefresh: () =>
    request<{ started: boolean }>(`/api/admin/cold-storage/refresh`, { method: 'POST' }),

  adminCancelQueuedJobs: () =>
    request<{ canceled: number }>(`/api/admin/jobs/cancel-queued`, { method: 'POST' }),

  // Create a new stream depot end-to-end (depot spec + mainline
  // stream + bootstrap .p4ignore submit). Admin-only.
  adminCreateDepot: (input: {
    name: string;
    description: string;
    ownerGroup?: string;
    streamDepth?: number;
    ignorePreset?: IgnorePreset;
  }) =>
    request<{ depot: string; stream: string; initialChange: number }>(
      `/api/admin/depot/create`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // List stream depots — for the Add Stream form's depot picker.
  adminStreamDepots: () =>
    request<{ depots: Array<{ name: string; type: string }> }>(
      `/api/admin/stream-depots`,
    ),

  // List streams under a depot — for the Add Stream form's parent
  // picker. Returns empty if the depot has no streams yet.
  adminStreamsInDepot: (depot: string) =>
    request<{
      streams: Array<{ stream: string; name: string; type: string; parent: string }>;
    }>(`/api/admin/streams?depot=${encodeURIComponent(depot)}`),

  // Add a stream to an existing depot.
  adminCreateStream: (input: {
    depot: string;
    name: string;
    type: 'mainline' | 'development' | 'release';
    description: string;
    parent?: string;
    populate?: boolean;
    ignorePreset?: IgnorePreset;
  }) =>
    request<{ stream: string; populatedChange: number | null }>(
      `/api/admin/stream/create`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  adminActions: (limit = 20) =>
    request<{ actions: AdminAction[] }>(`/api/admin/actions?limit=${limit}`),

  // Bulk download: programmatic form-POST. fetch+blob would buffer
  // multi-GB ZIPs in JS memory; a real form submit hands the response
  // to the browser's download pipeline, which streams to disk.
  downloadZip(paths: string[]): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/depot/download-zip';
    form.style.display = 'none';
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'paths';
    input.value = JSON.stringify(paths);
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    form.remove();
  },
};

export type DamJobState = 'queued' | 'running' | 'done' | 'failed';

// Response shape for /api/dam/asset-preview — the unified read of
// dam_jobs row + cache inventory + meta blob + current renderer code
// version. Both the frontend's useDamReady hook and ops scripts
// consume this same shape so they stay in lockstep.
export interface DamAssetPreviewResponse {
  path: string;
  rev: number;
  phase: 'ready' | 'rendering' | 'no-preview' | 'failed' | 'idle';
  isStale: boolean;
  currentRendererCodeVersion: number;
  job: {
    id: number;
    state: DamJobState;
    attempts: number;
    error: string | null;
    rendererCodeVersion: number | null;
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
  } | null;
  cache: {
    meta: { has: boolean; cacheSchemaVersion: number | null };
    preview: { has: boolean; ext: string | null; size: number | null };
    thumbnails: Array<{ size: 256 | 512; bytes: number }>;
  };
  meta: Record<string, unknown> | null;
}

export interface DamPendingResponse {
  jobId: number;
  state: DamJobState;
}

export interface LockEntry {
  depotPath: string;
  rev: number | null;
  action: string | null;
  type: string | null;
  user: string | null;
  client: string | null;
  change: string | null;
}

export interface LocksResponse {
  path: string;
  locks: LockEntry[];
}

export interface HistoryEntry {
  rev: number;
  change: number | null;
  action: string | null;
  user: string | null;
  client: string | null;
  type: string | null;
  time: string | null;
  fileSize: number | null;
  description: string | null;
}

export interface HistoryResponse {
  path: string;
  entries: HistoryEntry[];
}

export interface TagEntry {
  tag: string;
  createdBy: string;
  createdAt: string;
}

export interface TagsResponse {
  path: string;
  tags: TagEntry[];
}

export interface AssetClassResponse {
  depotPath: string;
  assetClass: string;
  headRev: number | null;
  detectedAt: string;
}

export interface DepotActivityResponse {
  activity: Array<{ date: string; count: number }>;
}

export interface SeatsResponse {
  users: { count: number | null; limit: number | null };
  clients: { count: number | null; limit: number | null };
}

export interface ContentHealthResponse {
  brokenRefs: number;
  overBudget: { total: number; texture: number; mesh: number };
  indexedAssets: number;
}

export interface StatusLocksResponse {
  locks: Array<{
    depotPath: string;
    rev: number | null;
    action: string | null;
    user: string | null;
    client: string | null;
  }>;
}

export interface FacetsResponse {
  path: string;
  classes: Array<{ name: string; count: number }>;
  tags: Array<{ name: string; count: number }>;
  // Budget facet counts (D-5). `large` is the warn+over superset of `over`.
  budget?: { over: number; large: number };
  // Count of assets with ≥1 unresolved import.
  brokenRefs?: number;
}

export interface JobsSummary {
  counts: { queued: number; running: number; done: number; failed: number };
  completedLastHour: number;
  // Done jobs rendered by an older RENDERER_CODE_VERSION → pending lazy re-render.
  staleCount: number;
  // Renders completed per hour over the last 24h (dense, oldest→newest).
  hourlyDone: number[];
  running: Array<{
    id: number;
    depotPath: string;
    headRev: number;
    kind: string;
    startedAt: string | null;
  }>;
  recentFailures: Array<{
    // Highest-id failed row in the (depot_path, head_rev, kind) group
    // — used as the canonical id for per-asset actions like retry.
    latestId: number;
    depotPath: string;
    headRev: number;
    kind: string;
    // Number of failed rows for this asset. With the partial dedupe
    // index covering only (queued, running, done), each retry by
    // backfill/trigger inserts a new failed row, so rowCount > 1
    // means "we tried to render this N times".
    rowCount: number;
    // Sum of per-row attempts across the group. Equals rowCount when
    // every retry was a fresh enqueue; can be higher if MAX_ATTEMPTS
    // > 1 led to in-row retries before the row transitioned.
    totalAttempts: number;
    error: string | null;
    finishedAt: string | null;
  }>;
}

export interface AddTagResponse {
  path: string;
  tag: string;
}

export interface StorageSummary {
  takenAt: string;
  filesystem: {
    mountPath: string;
    totalBytes: number;
    freeBytes: number;
    usedBytes: number;
  };
  damCache: {
    bytes: number;
    files: number;
    assetDirs: number;
  };
  // Sum and per-component breakdown of host-side storage taken by
  // the Perforce stack itself, vs. everything else on the Colima
  // VM disk (system, other apps, build caches, etc).
  perforce: {
    p4dDataBytes: number;
    p4dDataFiles: number;
    checkpointsBytes: number;
    checkpointsFiles: number;
    // p4d data + checkpoints + dam-cache.
    totalBytes: number;
  };
}

export interface TopAsset {
  depotPath: string;
  bytes: number;
  revs: number;
  latestRev: number;
}

export type TagCategory = 'descriptive' | 'administrative' | 'operational';

export interface AdminTag {
  id: number;
  name: string;
  useCount: number;
  // AEM three-bucket classification — null means uncategorized.
  category: TagCategory | null;
  createdAt: string;
}

export interface TagAuditEntry {
  id: number;
  action: 'rename' | 'merge' | 'delete';
  sourceName: string;
  targetName: string | null;
  affectedAssignments: number;
  createdAt: string;
  createdBy: string;
}

export interface BackupSnapshot {
  id: number;
  takenAt: string;
  totalBytes: number;
  totalObjects: number;
  checkpointsBytes: number;
  checkpointsObjects: number;
  archivesBytes: number;
  archivesObjects: number;
  lastRunStartedAt: string | null;
  lastRunEndedAt: string | null;
  lastRunStatus: 'success' | 'failed' | 'in-progress' | null;
  lastRunErrorTail: string | null;
  latestCheckpointName: string | null;
  latestCheckpointModifiedAt: string | null;
  snapshotMs: number;
}

export interface BackupLogTail {
  available: boolean;
  path?: string;
  bytes?: number;
  lines: string[];
  lastRunStartedAt?: string | null;
  lastRunEndedAt?: string | null;
  lastRunStatus?: 'success' | 'failed' | 'in-progress' | null;
}

export interface BackupHistoryPoint {
  takenAt: string;
  totalBytes: number;
  totalObjects: number;
  checkpointsBytes: number;
  archivesBytes: number;
  lastRunStatus: 'success' | 'failed' | 'in-progress' | null;
  latestCheckpointModifiedAt: string | null;
}

export interface BackupHistory {
  windowHours: number;
  points: BackupHistoryPoint[];
}
